import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import PatientManagementList from '../components/patientmanagement/PatientManagementList';
import { patientManagementService } from '../services/patientManagementService';
import { toast } from 'react-toastify';
import { FaSearch, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const PatientManagementPage = () => {
  const [profiles, setProfiles] = useState([]);
  const [healthGoals, setHealthGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const LIMIT = 10;

  // Fetch profiles
  const fetchProfiles = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const response = await patientManagementService.getProfilesList(
        page,
        LIMIT,
        search
      );
      setProfiles(response.data || []);
      setPagination(response.pagination);
      setCurrentPage(page);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch profiles');
      console.error('Error:', error);
      setLoading(false);
    }
  };

  // Fetch health goals
  const fetchHealthGoals = async () => {
    try {
      const goals = await patientManagementService.getAllHealthGoals();
      setHealthGoals(goals);
    } catch (error) {
      toast.error('Failed to fetch health goals');
      console.error('Error:', error);
    }
  };

  // Initial load
  useEffect(() => {
    fetchHealthGoals();
    fetchProfiles(1, '');
  }, []);

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    setIsSearching(true);
    
    // Debounce search
    const timer = setTimeout(() => {
      fetchProfiles(1, value);
      setIsSearching(false);
    }, 500);

    return () => clearTimeout(timer);
  };

  // Handle start goal
  const handleStartGoal = async (profileId, goalId) => {
    try {
      const response = await patientManagementService.startGoal(profileId, goalId);
      console.log('Start goal response:', response);
      
      // Show success toast
      toast.success(response?.message || 'Goal started successfully');
      
      // Refresh profiles to update the card
      await fetchProfiles(currentPage, searchTerm);
    } catch (error) {
      console.error('Error starting goal:', error);
      toast.error(error.response?.data?.message || 'Failed to start goal');
    }
  };

  // Handle end goal
  const handleEndGoal = async (profileId) => {
    if (window.confirm('Are you sure you want to end the current goal?')) {
      try {
        const response = await patientManagementService.endGoal(profileId);
        console.log('End goal response:', response);
        
        // Show success toast
        toast.success(response?.message || 'Goal ended successfully');
        
        // Refresh profiles to update the card
        await fetchProfiles(currentPage, searchTerm);
      } catch (error) {
        console.error('Error ending goal:', error);
        toast.error(error.response?.data?.message || 'Failed to end goal');
      }
    }
  };

  // Handle pagination
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      fetchProfiles(currentPage - 1, searchTerm);
    }
  };

  const handleNextPage = () => {
    if (pagination && currentPage < pagination.totalPages) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      fetchProfiles(currentPage + 1, searchTerm);
    }
  };

  const handlePageClick = (page) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    fetchProfiles(page, searchTerm);
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Patient Management
          </h1>
          <p className="text-gray-600">
            Manage patient profiles and their health goals
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Search by name or phone number..."
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all shadow-sm"
            />
            {isSearching && (
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin">
                  <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full"></div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Profiles List */}
        <div className={`transition-opacity duration-300 ${loading ? 'opacity-50' : 'opacity-100'}`}>
          <PatientManagementList
            profiles={profiles}
            healthGoals={healthGoals}
            loading={loading}
            onStartGoal={handleStartGoal}
            onEndGoal={handleEndGoal}
            pagination={pagination}
          />
        </div>

        {/* Pagination Controls */}
        {pagination && pagination.totalPages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-orange-50 hover:border-orange-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Previous page"
            >
              <FaChevronLeft size={14} />
            </button>

            <div className="flex items-center gap-1">
              {/* First page */}
              <button
                onClick={() => handlePageClick(1)}
                className={`w-9 h-9 rounded-lg font-medium transition-colors ${
                  currentPage === 1
                    ? 'bg-orange-600 text-white'
                    : 'border border-gray-300 text-gray-700 hover:bg-orange-50 hover:border-orange-300'
                }`}
              >
                1
              </button>

              {/* Left ellipsis */}
              {currentPage > 3 && (
                <span className="px-2 text-gray-500">...</span>
              )}

              {/* Middle pages */}
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                .filter((page) => {
                  if (pagination.totalPages <= 5) return true;
                  if (page === 1 || page === pagination.totalPages) return false;
                  return Math.abs(page - currentPage) <= 1;
                })
                .map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageClick(page)}
                    className={`w-9 h-9 rounded-lg font-medium transition-colors ${
                      currentPage === page
                        ? 'bg-orange-600 text-white'
                        : 'border border-gray-300 text-gray-700 hover:bg-orange-50 hover:border-orange-300'
                    }`}
                  >
                    {page}
                  </button>
                ))}

              {/* Right ellipsis */}
              {currentPage < pagination.totalPages - 2 && (
                <span className="px-2 text-gray-500">...</span>
              )}

              {/* Last page */}
              {pagination.totalPages > 1 && (
                <button
                  onClick={() => handlePageClick(pagination.totalPages)}
                  className={`w-9 h-9 rounded-lg font-medium transition-colors ${
                    currentPage === pagination.totalPages
                      ? 'bg-orange-600 text-white'
                      : 'border border-gray-300 text-gray-700 hover:bg-orange-50 hover:border-orange-300'
                  }`}
                >
                  {pagination.totalPages}
                </button>
              )}
            </div>

            <button
              onClick={handleNextPage}
              disabled={currentPage === pagination.totalPages}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-orange-50 hover:border-orange-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Next page"
            >
              <FaChevronRight size={14} />
            </button>

            <span className="ml-4 text-sm text-gray-600 font-medium">
              Page <span className="text-orange-600">{currentPage}</span> of{' '}
              <span className="text-orange-600">{pagination.totalPages}</span>
            </span>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PatientManagementPage;
